import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
  line-height: 1.6;
  color: ${({ theme }) => theme.text};
  background-color: ${({ theme }) => theme.background}; 
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05); 
`;

const Section = styled.section`
  margin-bottom: 30px;
`;

const Heading = styled.h1`
  margin-bottom: 15px;
  text-align: center;
  color: ${({ theme }) => theme.text};
`;

const SubHeading = styled.h2`
  margin-top: 20px;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.text};
`;

const Paragraph = styled.p`
  margin-bottom: 10px;
  line-height: 1.6;
  color: ${({ theme }) => theme.text};
`;

const List = styled.ul`
  list-style-type: disc;
  margin-left: 20px;
  margin-bottom: 15px;
`;

const LinkList = styled.ul`
  list-style-type: none;
  margin-left: 20px; 
  padding-left: 0; 
  margin-bottom: 15px;

  a {
    color: ${({ theme }) => theme.headerText || '#007bff'};
    text-decoration: none;
    font-weight: bold;
  }
  a:hover {
    text-decoration: underline;
  }
`;

const ListItem = styled.li`
  margin-bottom: 5px;
  color: ${({ theme }) => theme.text};
`;

const Strong = styled.strong`
  font-weight: bold;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin-top: 15px;
  margin-bottom: 30px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
  background-color: ${({ theme }) => theme.tableBackground}; 
`;

const Th = styled.th`
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
  background-color: ${({ theme }) => theme.tableHeaderBackground};
  color: ${({ theme }) => theme.tableHeaderText};
`;

const Td = styled.td`
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
  vertical-align: top; 
  color: ${({ theme }) => theme.tableCellText};
`;

const TemplateFieldsTable = ({ title, fields }) => (
  <Section>
    <SubHeading>{title}</SubHeading>
    <TableWrapper>
      <Table>
        <thead>
          <tr>
            <Th>Column #</Th>
            <Th>Classify Field Setup</Th>
            <Th>Target Field Category</Th>
            <Th>Target Column Type</Th>
            <Th>Target Column Name</Th>
            <Th>Source Column Name</Th>
            <Th>Column Not Used</Th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field, index) => (
            <tr key={index}>
              <Td>{field.column_number}</Td>
              <Td>{field.target_column_classify_field_setup}</Td>
              <Td>{field.target_field_category}</Td>
              <Td>{field.target_column_type}</Td>
              <Td>{field.target_column_name}</Td>
              <Td>{field.source_column_name}</Td>
              <Td>{field.column_not_used}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableWrapper>
  </Section>
);

const Fhms2025Content = () => {
  // Field mappings pulled dynamically via json_agg(row_to_json(the_data)) for template 123
  const coreTemplateFields = [
    {
        "column_number": 1,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_survey_production_lot_id",
        "target_column_name": "csb_survey_production_lot_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 2,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "feedlot_code",
        "target_column_name": "csb_survey_main_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 3,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_record_group_id",
        "target_column_name": "csb_record_group_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 4,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_account_id",
        "target_column_name": "csb_account_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 5,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_survey_production_lot_upload_id",
        "target_column_name": "csb_survey_production_lot_upload_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 6,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_template_id",
        "target_column_name": "csb_pl_template_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 7,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "production_lot_code",
        "target_column_name": "csb_pl_production_lot_code",
        "target_column_type": "varchar(128)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 8,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "sex",
        "target_column_name": "csb_pl_sex_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 9,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "steers_pct",
        "target_column_name": "csb_pl_sex_percent_steers",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 10,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_sex_percent_cows",
        "target_column_name": "csb_pl_sex_percent_cows",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 11,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "heifers_pct",
        "target_column_name": "csb_pl_sex_percent_heifers",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 12,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_sex_percent_bulls",
        "target_column_name": "csb_pl_sex_percent_bulls",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 13,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "calf_or_yearling",
        "target_column_name": "csb_pl_animal_age_category_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 14,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "calf_pct",
        "target_column_name": "csb_pl_percent_calves",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 15,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "yearling_pct",
        "target_column_name": "csb_pl_percent_yearlings",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 16,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_percent_adults",
        "target_column_name": "csb_pl_percent_adults",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 17,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "origin",
        "target_column_name": "csb_pl_origin_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 18,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "auction_pct",
        "target_column_name": "csb_pl_origin_percent_auction",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 19,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "backgrounded_pct",
        "target_column_name": "csb_pl_origin_percent_backgrounded",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 20,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "ranch_direct_pct",
        "target_column_name": "csb_pl_origin_percent_ranch_direct",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 21,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "brd_risk",
        "target_column_name": "csb_pl_brd_risk_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 22,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "low_brd_risk_pct",
        "target_column_name": "csb_pl_brd_risk_percent_low",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 23,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_brd_risk_percent_medium",
        "target_column_name": "csb_pl_brd_risk_percent_medium",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 24,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "high_brd_risk_pct",
        "target_column_name": "csb_pl_brd_risk_percent_high",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 25,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "quarter_of_close_out",
        "target_column_name": "csb_pl_quarter_of_year_closeout_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 26,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "animals_in",
        "target_column_name": "csb_pl_n_animals_in",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 27,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_n_animals_railed",
        "target_column_name": "csb_pl_n_animals_railed",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 28,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "animals_dead",
        "target_column_name": "csb_pl_n_animals_dead",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 29,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "animals_slaughtered",
        "target_column_name": "csb_pl_n_animals_slaughtered",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 30,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_source_weight_in_units_id",
        "target_column_name": "csb_pl_source_weight_in_units_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 31,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_source_weight_in_value",
        "target_column_name": "csb_pl_source_weight_in_value",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 32,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "average_weight_in_kg",
        "target_column_name": "csb_pl_avg_weight_in_kgs",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Multiple Target Fields (3)"
    },
    {
        "column_number": 33,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_source_weight_out_units_id",
        "target_column_name": "csb_pl_source_weight_out_units_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 34,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_source_weight_out_value",
        "target_column_name": "csb_pl_source_weight_out_value",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 35,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "average_weight_out_kg",
        "target_column_name": "csb_pl_avg_weight_out_kgs",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Multiple Target Fields (3)"
    },
    {
        "column_number": 36,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "average_date_in",
        "target_column_name": "csb_pl_avg_date_in",
        "target_column_type": "date",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 37,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "average_date_out",
        "target_column_name": "csb_pl_avg_date_out",
        "target_column_type": "date",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 38,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "dof",
        "target_column_name": "csb_pl_total_days_on_feed",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 39,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "head_days_calculated",
        "target_column_name": "csb_pl_total_head_days",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 40,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_notes",
        "target_column_name": "csb_pl_notes",
        "target_column_type": "varchar",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 41,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "breed_type",
        "target_column_name": "csb_pl_breed_type_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 42,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_breed_type_percent_beef",
        "target_column_name": "csb_pl_breed_type_percent_beef",
        "target_column_type": "numeric(18,8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 43,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_breed_type_percent_dairy",
        "target_column_name": "csb_pl_breed_type_percent_dairy",
        "target_column_type": "numeric(18,8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 44,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_breed_type_percent_beef_dairy",
        "target_column_name": "csb_pl_breed_type_percent_beef_dairy",
        "target_column_type": "numeric(18,8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 45,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "average_days_on_feed",
        "target_column_name": "csb_pl_source_total_days_on_feed",
        "target_column_type": "numeric(18,8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 46,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "country_of_origin",
        "target_column_name": "csb_country_of_origin_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 47,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "brd_deads",
        "target_column_name": "mortality_brd_deads_source_value",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 48,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "mortality_units",
        "target_column_name": "mortality_brd_deads_source_units_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 49,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "brd_deads",
        "target_column_name": "mortality_brd_n_deads",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 50,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "mortality_brd_pct_deads",
        "target_column_name": "mortality_brd_pct_deads",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 51,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "metabolic_deads",
        "target_column_name": "mortality_metabolic_deads_source_value",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 52,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "mortality_units",
        "target_column_name": "mortality_metabolic_deads_source_units_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 53,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "metabolic_deads",
        "target_column_name": "mortality_metabolic_n_deads",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 54,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "mortality_metabolic_pct_deads",
        "target_column_name": "mortality_metabolic_pct_deads",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 55,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "histophilosis_deads",
        "target_column_name": "mortality_histophilosis_deads_source_value",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 56,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "mortality_units",
        "target_column_name": "mortality_histophilosis_deads_source_units_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 57,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "histophilosis_deads",
        "target_column_name": "mortality_histophilosis_n_deads",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 58,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "mortality_histophilosis_pct_deads",
        "target_column_name": "mortality_histophilosis_pct_deads",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 59,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "lameness_deads",
        "target_column_name": "mortality_lameness_deads_source_value",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 60,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "mortality_units",
        "target_column_name": "mortality_lameness_deads_source_units_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 61,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "lameness_deads",
        "target_column_name": "mortality_lameness_n_deads",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 62,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "mortality_lameness_pct_deads",
        "target_column_name": "mortality_lameness_pct_deads",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 63,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "other_deads",
        "target_column_name": "mortality_other_deads_source_value",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 64,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "mortality_units",
        "target_column_name": "mortality_other_deads_source_units_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 65,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "other_deads",
        "target_column_name": "mortality_other_n_deads",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 66,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "mortality_other_pct_deads",
        "target_column_name": "mortality_other_pct_deads",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 67,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "animals_dead",
        "target_column_name": "mortality_total_n_deads",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 68,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "mortality_notes",
        "target_column_name": "mortality_notes",
        "target_column_type": "text",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 69,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "brd_treat_1st_pull",
        "target_column_name": "treatment_brd_source_value",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 70,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "morbidity_units",
        "target_column_name": "treatment_brd_source_units_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 71,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "brd_treat_1st_pull",
        "target_column_name": "treatment_n_brd",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 72,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "treatment_pct_brd",
        "target_column_name": "treatment_pct_brd",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 73,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "metabolic_treat_1st_pull",
        "target_column_name": "treatment_metabolic_source_value",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 74,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "morbidity_units",
        "target_column_name": "treatment_metabolic_source_units_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 75,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "metabolic_treat_1st_pull",
        "target_column_name": "treatment_n_metabolic",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 76,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "treatment_pct_metabolic",
        "target_column_name": "treatment_pct_metabolic",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 77,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "lameness_treat_1st_pull",
        "target_column_name": "treatment_lameness_source_value",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 78,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "morbidity_units",
        "target_column_name": "treatment_lameness_source_units_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 79,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "lameness_treat_1st_pull",
        "target_column_name": "treatment_n_lameness",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 80,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "treatment_pct_lameness",
        "target_column_name": "treatment_pct_lameness",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 81,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "treatment_other_source_value",
        "target_column_name": "treatment_other_source_value",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 82,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "treatment_other_source_units_id",
        "target_column_name": "treatment_other_source_units_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 83,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "treatment_n_other",
        "target_column_name": "treatment_n_other",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 84,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "treatment_pct_other",
        "target_column_name": "treatment_pct_other",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 85,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "treatment_n_total",
        "target_column_name": "treatment_n_total",
        "target_column_type": "numeric(18, 8)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 86,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "treatment_notes",
        "target_column_name": "treatment_notes",
        "target_column_type": "text",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    }
  ];

  const feedDrugTemplateFields = [
    {
        "column_number": 1,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "lot_id",
        "target_column_name": "csb_survey_production_lot_drug_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 2,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_survey_production_lot_id",
        "target_column_name": "csb_survey_production_lot_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 3,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_survey_main_id",
        "target_column_name": "csb_survey_main_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 4,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_record_group_id",
        "target_column_name": "csb_record_group_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 5,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_account_id",
        "target_column_name": "csb_account_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 6,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_survey_production_lot_upload_id",
        "target_column_name": "csb_survey_production_lot_upload_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 7,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_template_id",
        "target_column_name": "csb_pl_template_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 8,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_ordinal",
        "target_column_name": "csb_pl_drug_ordinal",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 9,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "route_of_admin",
        "target_column_name": "csb_pl_drug_route_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 10,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "active_ingredient",
        "target_column_name": "csb_pl_source_drug_name",
        "target_column_type": "varchar(192)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 11,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "total_quantity_of_antimicrobial_ingredient_used_in_g",
        "target_column_name": "csb_pl_source_drug_value",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 12,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "total_active_ingredient_units",
        "target_column_name": "csb_pl_source_drug_value_units_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 13,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_trade_name_id",
        "target_column_name": "csb_pl_drug_trade_name_id",
        "target_column_type": "integer",
        "target_field_category": "Trade Name (2)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 14,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "active_ingredient",
        "target_column_name": "csb_pl_drug_active_ingredient_id",
        "target_column_type": "integer",
        "target_field_category": "Active Ingredient (1)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 15,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_total_mg",
        "target_column_name": "csb_pl_drug_total_mg",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 16,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_total_ml",
        "target_column_name": "csb_pl_drug_total_ml",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 17,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_regimen_id",
        "target_column_name": "csb_pl_drug_regimen_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 18,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_regimen_detail_id",
        "target_column_name": "csb_pl_drug_regimen_detail_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 19,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "reason_for_use",
        "target_column_name": "csb_pl_drug_primary_reason_for_use",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 20,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_primary_reason_for_use_pct_gp",
        "target_column_name": "csb_pl_drug_primary_reason_for_use_pct_gp",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 21,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_primary_reason_for_use_pct_dp",
        "target_column_name": "csb_pl_drug_primary_reason_for_use_pct_dp",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 22,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_primary_reason_for_use_pct_dt",
        "target_column_name": "csb_pl_drug_primary_reason_for_use_pct_dt",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 23,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "condition_antimicrobial_used_for",
        "target_column_name": "csb_pl_drug_secondary_reason_respiratory",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 24,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_digestive",
        "target_column_name": "csb_pl_drug_secondary_reason_digestive",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 25,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_lameness",
        "target_column_name": "csb_pl_drug_secondary_reason_lameness",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 26,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_neurologic",
        "target_column_name": "csb_pl_drug_secondary_reason_neurologic",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 27,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_liver_abscess",
        "target_column_name": "csb_pl_drug_secondary_reason_liver_abscess",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 28,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_coccidiosis",
        "target_column_name": "csb_pl_drug_secondary_reason_coccidiosis",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 29,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_enteritis",
        "target_column_name": "csb_pl_drug_secondary_reason_enteritis",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 30,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_mixed",
        "target_column_name": "csb_pl_drug_secondary_reason_mixed",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 31,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_mixed_description",
        "target_column_name": "csb_pl_drug_secondary_reason_mixed_description",
        "target_column_type": "varchar(128)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 32,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_other",
        "target_column_name": "csb_pl_drug_secondary_reason_other",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 33,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_other_description",
        "target_column_name": "csb_pl_drug_secondary_reason_other_description",
        "target_column_type": "varchar(128)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 34,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "condition_antimicrobial_used_for",
        "target_column_name": "csb_pl_drug_secondary_reason_source",
        "target_column_type": "varchar",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 35,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_notes",
        "target_column_name": "csb_pl_drug_notes",
        "target_column_type": "varchar",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    }
  ];

  // 3. INJECTABLE DRUG TEMPLATE FIELDS (Template ID: 125)
  // Run SQL query with: where tf.csb_data_template_id = 125
  const injectableDrugTemplateFields = [
    {
        "column_number": 1,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "lot_id",
        "target_column_name": "csb_survey_production_lot_drug_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 2,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_survey_production_lot_id",
        "target_column_name": "csb_survey_production_lot_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 3,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_survey_main_id",
        "target_column_name": "csb_survey_main_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 4,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_record_group_id",
        "target_column_name": "csb_record_group_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 5,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_account_id",
        "target_column_name": "csb_account_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 6,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_survey_production_lot_upload_id",
        "target_column_name": "csb_survey_production_lot_upload_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 7,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_template_id",
        "target_column_name": "csb_pl_template_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 8,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_ordinal",
        "target_column_name": "csb_pl_drug_ordinal",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 9,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "route_of_admin",
        "target_column_name": "csb_pl_drug_route_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 10,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "active_ingredient",
        "target_column_name": "csb_pl_source_drug_name",
        "target_column_type": "varchar(192)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 11,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "total_quantity_of_antimicrobial_used_in_ml",
        "target_column_name": "csb_pl_source_drug_value",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 12,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "drug_units",
        "target_column_name": "csb_pl_source_drug_value_units_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 13,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "product_names",
        "target_column_name": "csb_pl_drug_trade_name_id",
        "target_column_type": "integer",
        "target_field_category": "Trade Name (2)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 14,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "active_ingredient",
        "target_column_name": "csb_pl_drug_active_ingredient_id",
        "target_column_type": "integer",
        "target_field_category": "Active Ingredient (1)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 15,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_total_mg",
        "target_column_name": "csb_pl_drug_total_mg",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 16,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_total_ml",
        "target_column_name": "csb_pl_drug_total_ml",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 17,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_regimen_id",
        "target_column_name": "csb_pl_drug_regimen_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 18,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_regimen_detail_id",
        "target_column_name": "csb_pl_drug_regimen_detail_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 19,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "reason_for_use",
        "target_column_name": "csb_pl_drug_primary_reason_for_use",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 20,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_primary_reason_for_use_pct_gp",
        "target_column_name": "csb_pl_drug_primary_reason_for_use_pct_gp",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 21,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_primary_reason_for_use_pct_dp",
        "target_column_name": "csb_pl_drug_primary_reason_for_use_pct_dp",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 22,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_primary_reason_for_use_pct_dt",
        "target_column_name": "csb_pl_drug_primary_reason_for_use_pct_dt",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 23,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "condition_antimicrobial_used_for",
        "target_column_name": "csb_pl_drug_secondary_reason_respiratory",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 24,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_digestive",
        "target_column_name": "csb_pl_drug_secondary_reason_digestive",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 25,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_lameness",
        "target_column_name": "csb_pl_drug_secondary_reason_lameness",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 26,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_neurologic",
        "target_column_name": "csb_pl_drug_secondary_reason_neurologic",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 27,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_liver_abscess",
        "target_column_name": "csb_pl_drug_secondary_reason_liver_abscess",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 28,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_coccidiosis",
        "target_column_name": "csb_pl_drug_secondary_reason_coccidiosis",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 29,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_enteritis",
        "target_column_name": "csb_pl_drug_secondary_reason_enteritis",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 30,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_mixed",
        "target_column_name": "csb_pl_drug_secondary_reason_mixed",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 31,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_mixed_description",
        "target_column_name": "csb_pl_drug_secondary_reason_mixed_description",
        "target_column_type": "varchar(128)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 32,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_other",
        "target_column_name": "csb_pl_drug_secondary_reason_other",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 33,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_other_description",
        "target_column_name": "csb_pl_drug_secondary_reason_other_description",
        "target_column_type": "varchar(128)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 34,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "condition_antimicrobial_used_for",
        "target_column_name": "csb_pl_drug_secondary_reason_source",
        "target_column_type": "varchar",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 35,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_notes",
        "target_column_name": "csb_pl_drug_notes",
        "target_column_type": "varchar",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    }
  ];

  // 4. BOLUS DRUG TEMPLATE FIELDS (Template ID: 126)
  // Run SQL query with: where tf.csb_data_template_id = 126
  const bolusDrugTemplateFields = [
    {
        "column_number": 1,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "pen_id",
        "target_column_name": "csb_survey_production_lot_drug_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 2,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_survey_production_lot_id",
        "target_column_name": "csb_survey_production_lot_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 3,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_survey_main_id",
        "target_column_name": "csb_survey_main_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 4,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_record_group_id",
        "target_column_name": "csb_record_group_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 5,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_account_id",
        "target_column_name": "csb_account_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 6,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_survey_production_lot_upload_id",
        "target_column_name": "csb_survey_production_lot_upload_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 7,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_template_id",
        "target_column_name": "csb_pl_template_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 8,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_ordinal",
        "target_column_name": "csb_pl_drug_ordinal",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 9,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "route_of_admin",
        "target_column_name": "csb_pl_drug_route_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 10,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "active_ingredient",
        "target_column_name": "csb_pl_source_drug_name",
        "target_column_type": "varchar(192)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 11,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "total_quantity_of_boluses_used",
        "target_column_name": "csb_pl_source_drug_value",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 12,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "bolus_units",
        "target_column_name": "csb_pl_source_drug_value_units_id",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 13,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "product_name",
        "target_column_name": "csb_pl_drug_trade_name_id",
        "target_column_type": "integer",
        "target_field_category": "Trade Name (2)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 14,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "active_ingredient",
        "target_column_name": "csb_pl_drug_active_ingredient_id",
        "target_column_type": "integer",
        "target_field_category": "Active Ingredient (1)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 15,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_total_mg",
        "target_column_name": "csb_pl_drug_total_mg",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 16,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_total_ml",
        "target_column_name": "csb_pl_drug_total_ml",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 17,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_regimen_id",
        "target_column_name": "csb_pl_drug_regimen_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 18,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_regimen_detail_id",
        "target_column_name": "csb_pl_drug_regimen_detail_id",
        "target_column_type": "bigint",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 19,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "reason_for_use",
        "target_column_name": "csb_pl_drug_primary_reason_for_use",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Lookup Field (2)"
    },
    {
        "column_number": 20,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_primary_reason_for_use_pct_gp",
        "target_column_name": "csb_pl_drug_primary_reason_for_use_pct_gp",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 21,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_primary_reason_for_use_pct_dp",
        "target_column_name": "csb_pl_drug_primary_reason_for_use_pct_dp",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 22,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_primary_reason_for_use_pct_dt",
        "target_column_name": "csb_pl_drug_primary_reason_for_use_pct_dt",
        "target_column_type": "numeric",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 23,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "condition_antimicrobial_used_for",
        "target_column_name": "csb_pl_drug_secondary_reason_respiratory",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 24,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_digestive",
        "target_column_name": "csb_pl_drug_secondary_reason_digestive",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 25,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_lameness",
        "target_column_name": "csb_pl_drug_secondary_reason_lameness",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 26,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_neurologic",
        "target_column_name": "csb_pl_drug_secondary_reason_neurologic",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 27,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_liver_abscess",
        "target_column_name": "csb_pl_drug_secondary_reason_liver_abscess",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 28,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_coccidiosis",
        "target_column_name": "csb_pl_drug_secondary_reason_coccidiosis",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 29,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_enteritis",
        "target_column_name": "csb_pl_drug_secondary_reason_enteritis",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 30,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_mixed",
        "target_column_name": "csb_pl_drug_secondary_reason_mixed",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 31,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_mixed_description",
        "target_column_name": "csb_pl_drug_secondary_reason_mixed_description",
        "target_column_type": "varchar(128)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 32,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_other",
        "target_column_name": "csb_pl_drug_secondary_reason_other",
        "target_column_type": "integer",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 33,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_secondary_reason_other_description",
        "target_column_name": "csb_pl_drug_secondary_reason_other_description",
        "target_column_type": "varchar(128)",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 34,
        "transformation": null,
        "column_not_used": "Used (0)",
        "source_column_name": "condition_antimicrobial_used_for",
        "target_column_name": "csb_pl_drug_secondary_reason_source",
        "target_column_type": "varchar",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    },
    {
        "column_number": 35,
        "transformation": null,
        "column_not_used": "Not Used (1)",
        "source_column_name": "csb_pl_drug_notes",
        "target_column_name": "csb_pl_drug_notes",
        "target_column_type": "varchar",
        "target_field_category": "Core Field (0)",
        "target_column_classify_field_setup": "Setup Standard Field (1)"
    }
  ];

  return (
    <Container>
      <Section>
        <Heading>
          Production Lot Upload Data Preparation: Feedlot Health Management Services (FHMS)
          <br />
          Site Information Details
        </Heading>
        <Paragraph>Feedlot Health Management Services (4) (FHMS)</Paragraph>
        <Paragraph>Vet: CSBV00001 (1)</Paragraph>
        <Paragraph>Project Year: 2025</Paragraph>
        <Paragraph>
          <Strong>Consolidation Note:</Strong> Coaldale Veterinary Clinic (CVC) is now integrated under Feedlot Health Management Services. All Coaldale herds are processed using the standard FHMS layout.
        </Paragraph>

        <Paragraph>Access to previous years documentation click on the appropriate link below:</Paragraph>
        <LinkList>
          <ListItem><Link to="/beef/data-preparation/fhms2019">FHMS 2019</Link></ListItem>
          <ListItem><Link to="/beef/data-preparation/fhms2020">FHMS 2020</Link></ListItem>
          <ListItem><Link to="/beef/data-preparation/fhms2021">FHMS 2021</Link></ListItem>
          <ListItem><Link to="/beef/data-preparation/fhms2022">FHMS 2022</Link></ListItem>
          <ListItem><Link to="/beef/data-preparation/fhms2023">FHMS 2023</Link></ListItem>
          <ListItem><Link to="/beef/data-preparation/fhms2024">FHMS 2024</Link></ListItem>
        </LinkList>

        <SubHeading>Project Context</SubHeading>
        <Paragraph>
          Preparing FHMS 2025 core and drug data for Vet 1 (CSBV00001). Data is for research year 2025 and is adapted from earlier models. FHMS dataset structure differs from VAHS/CVC, so upload processing is handled differently. The work process is: <Strong>copy source → prepare and clean in a Work worksheet → produce a final “… To Upload” worksheet</Strong> named with the vet/herd/year/account info. With this layout we require four templates to be created.
        </Paragraph>

        <section>
          <h2 className="font-semibold mt-6">
            Feedlot Health Management Services (FHMS) – Group Id = 1
          </h2>
          <p>
            <strong>Source:</strong> Workbooks including FHMS and consolidated Coaldale herds.
          </p>
          <h3 className="font-semibold mt-4">Worksheets:</h3>
          <ul className="list-disc pl-6">
            <li>Core data (all herds).</li>
            <li>Drug data – Feed (all herds).</li>
            <li>Drug data – Injectable (all herds).</li>
            <li>Drug data – Bolus (all herds).</li>
          </ul>
          <h3 className="font-semibold mt-4">Data Layout:</h3>
          <ul className="list-disc pl-6">
            <li>Each drug record (feed, injectable, or bolus) must match with a production lot code in core data.</li>
            <li>This ensures related records stay properly linked.</li>
          </ul>
          <h3 className="font-semibold mt-4">Templates:</h3>
          <ul className="list-disc pl-6">
            <li>1 template for core data.</li>
            <li>1 template for feed drug data.</li>
            <li>1 template for injectable drug data.</li>
            <li>1 template for bolus drug data.</li>
          </ul>
          <h3 className="font-semibold mt-4">Scripts:</h3>
          <p>
            Require specifying the correct template and associated worksheet for each type (core,
            feed, injectable, bolus). Upload, integrate, then prepare reports.
          </p>
        </section>

        <Paragraph>
          <Strong>***The integrity of the template is about keeping the spreadsheets aligned with what the application expects —
          correct columns, names, units, and validation checks. FHMS data is structured differently than past uploads,
          so extra steps are needed to prepare it without breaking those template rules. These notes explain the
          adjustments made to ensure the upload process works smoothly and consistently.***</Strong>
        </Paragraph>

        <hr />

        <section>
          <h2>Data Prep & Cleaning</h2>
          <ol>
            <li>Check for hidden rows/columns in each <em>… To Upload</em> worksheet.</li>
            <li>Remove stray empty columns/rows in each <em>… To Upload</em> sheet.</li>
            <li>
              Create a distinct herd code list: copy <em>Feedlot code</em> from Core To
              Upload → paste into a new sheet <strong>FHMS 2025 Herd Codes</strong> → Data
              → Remove Duplicates → get a unique herd list (used to pre-create survey
              records).
            </li>
          </ol>
        </section>

        <Section>
          <SubHeading>Core Data Preparation Steps</SubHeading>

          <Paragraph><Strong>1. Project Year Column</Strong></Paragraph>
          <List>
            <ListItem>Insert a new column A at the beginning of the core data worksheet.</ListItem>
            <ListItem>Name the column “Project Year”.</ListItem>
            <ListItem>For all rows, enter the year (e.g., 2025).</ListItem>
            <ListItem>This column may not be used in the upload template, but it’s good practice for tracking which year of data is being worked on.</ListItem>
          </List>

          <Paragraph><Strong>2. Breed Type</Strong></Paragraph>
          <List>
            <ListItem>After the column “Production lot code”, insert a new column named “Breed Type”.</ListItem>
            <ListItem>For all rows, enter the value “Beef”.</ListItem>
            <ListItem>This will need verification later but works for test uploads.</ListItem>
          </List>

          <Paragraph><Strong>3. Country of Origin</Strong></Paragraph>
          <List>
            <ListItem>After the “Breed Type” column, insert a new column named “Country of Origin”.</ListItem>
            <ListItem>For all rows, enter the value “Canada”.</ListItem>
            <ListItem>Although the system defaults to Canada if missing, adding it makes the data explicit.</ListItem>
          </List>

          <Paragraph><Strong>4. Date In</Strong></Paragraph>
          <List>
            <ListItem>The existing column “Average date in” should be preserved.</ListItem>
            <ListItem>Insert a new column beside it, also named “Average date in”.</ListItem>
            <ListItem>Rename the original to “Average date in original”.</ListItem>
            <ListItem>This provides clearer formatting for uploads.</ListItem>
          </List>

          <Paragraph><Strong>5. Date Out</Strong></Paragraph>
          <List>
            <ListItem>The existing column “Average date out” should be preserved.</ListItem>
            <ListItem>Insert a new column beside it, also named “Average date out”.</ListItem>
            <ListItem>Rename the original to “Average date out original”.</ListItem>
            <ListItem>This provides clearer formatting for uploads.</ListItem>
          </List>

          <Paragraph><Strong>6. Check Dates</Strong></Paragraph>
          <List>
            <ListItem>After “Average date out”, insert a new column named “Check Date”.</ListItem>
            <ListItem>Calculate values as: Check Date = DAYS([Average date out], [Average date in]).</ListItem>
            <ListItem>Format the results as numbers.</ListItem>
            <ListItem>Validation checks:</ListItem>
            <List>
              <ListItem>Values must always be greater than 0 (ensures dates are in order).</ListItem>
              <ListItem>Values should be equal or very close to “Days on feed”.</ListItem>
            </List>
          </List>

          <Paragraph><Strong>7. Arrival Weight Units</Strong></Paragraph>
          <List>
            <ListItem>After the column “Average weight in (kg)”, insert a new column named “Arrival Weight Units”.</ListItem>
            <ListItem>For all rows, enter the value “kgs”.</ListItem>
          </List>

          <Paragraph><Strong>8. Shipping Weight Units</Strong></Paragraph>
          <List>
            <ListItem>After the column “Average weight out (kg)”, insert a new column named “Shipping Weight Units”.</ListItem>
            <ListItem>For all rows, enter the value “kgs”.</ListItem>
          </List>

          <Paragraph><Strong>9. Check Number Shipped</Strong></Paragraph>
          <List>
            <ListItem>Insert a new column after “# animals dead” called “Check N Shipped”.</ListItem>
            <ListItem>Calculate as: Check N Shipped = # animals in - # animals dead (FHMS does not include railed, so ignore it.)</ListItem>
            <ListItem>Compare results to “# animals slaughtered”. They should be equal or very close.</ListItem>
          </List>

          <SubHeading>Core Data Finalization</SubHeading>
          <List>
            <ListItem>Copy the prepared core data from the worksheet “All Herds Work Data Core”.</ListItem>
            <ListItem>Paste into the upload sheet “V1 All 2025 G4 Core To Upload”.</ListItem>
            <ListItem>Core data is now complete and ready for the first upload test.</ListItem>
          </List>
        </Section>

        <Section>
          <hr />
          <h2>Drug Data Preparation</h2>

          <h3>Copying to Organize Source Drug Data to Appropriate Work Worksheets</h3>
          <ul>
            <li>
              <strong>Bolus</strong>
              <ul>
                <li>Source Worksheet: "All Herds - Bolus Source"</li>
                <li>Work Worksheet: "All Herds - Bolus Work"</li>
              </ul>
            </li>
            <li>
              <strong>Feed</strong>
              <ul>
                <li>Source Worksheet: "All Herds - Feed Source"</li>
                <li>Work Worksheet: "All Herds - Feed Work"</li>
                <li>Pasted as-is, adjusted column widths and row heights.</li>
                <li>Cleared formats for header and column names.</li>
              </ul>
            </li>
            <li>
              <strong>Injectable</strong>
              <ul>
                <li>Source Worksheet: "All Herds - Injectable Source"</li>
                <li>Work Worksheet: "All Herds - Injectable Work"</li>
                <li>Pasted as-is, adjusted column widths and row heights.</li>
                <li>Cleared formats for header and column names.</li>
              </ul>
            </li>
          </ul>

          <h3>Preparing Work Worksheets</h3>
          <ul>
            <li>
              <strong>Feed Drugs (Work)</strong>
              <ul>
                <li>Insert new column before "Comments".</li>
                <li>Column name: "Drug Units".</li>
                <li>Enter value "g" for all rows.</li>
                <li>Ready for upload (copy/paste to "… To Upload" worksheet).</li>
              </ul>
            </li>
            <li>
              <strong>Injectable Drugs (Work)</strong>
              <ul>
                <li>Insert new column after "Total quantity of antimicrobial used in mL".</li>
                <li>Column name: "Drug Units".</li>
                <li>Enter value "ml" for all rows.</li>
                <li>Ready for upload (copy/paste to "… To Upload" worksheet).</li>
              </ul>
            </li>
          </ul>

          <section>
            <h2>Preparing & Validating Upload Files</h2>
            <ul>
              <li>After cleaning, copy the <em>… Work</em> sheet into a new <em>… To Upload</em> sheet (use the naming convention above).</li>
              <li>Delete empty columns to the right and empty rows below the last data row to avoid stray data.</li>
              <li>Ensure header names exactly match the upload template field names (case/spacing consistent where uploader is strict).</li>
              <li>Save a backup copy of the final workbook before attempting upload (version as V2, V3 when making changes).</li>
              <li>Create templates mapping source field → target field (core, feed, injectable, bolus).</li>
              <li>If template changes are needed, save as next version and record what changed.</li>
              <li>Run an initial small upload test and validate results; adjust mapping/rules where mismatches occur.</li>
            </ul>
          </section>

          <hr />

          <Section>
            <h3>Additional Data Adjustments</h3>
            <ol>
              <li>
                <strong>Total Head Days (Updated)</strong>
                <ul>
                  <li>Go to the worksheet "All Herds Work Data Core".</li>
                  <li>Locate the column "Head days".</li>
                  <li>Insert a new column after it and name it "Head days".</li>
                  <li>Keep the same name so the template recognizes it automatically.</li>
                  <li>Rename the original "Head days" column to "Head days original".</li>
                  <li>In the new column, calculate the values for each row as:
                    <ul>
                      <li>New Total Head Days = V2 * (G2 - 0.5 * Q2)</li>
                    </ul>
                  </li>
                  <li>After calculating, copy the new "Head days" column into the upload worksheet "V1 All 2025 G4 Core To Upload".</li>
                  <li>The dataset is now ready for re-upload into the database.</li>
                </ul>
              </li>
              <li>
                <strong>Mortality / Morbidity Text Cleanup</strong>
                <ul>
                  <li>Go to "All Herds Work Data Core" worksheet.</li>
                  <li>Locate the mortality/morbidity columns near the end of the sheet.</li>
                  <li>Scroll through these columns; some cells contain text like "NA", "No Consent", etc.</li>
                  <li>Remove all non-numeric text so that each cell contains only numbers or empty cells.</li>
                </ul>
              </li>
              <li>
                <strong>Check and Recalculate “Other Deads”</strong>
                <ul>
                  <li>Compare Total Deads against the sum of individual mortality columns: BRD, Lameness, Histophilus, Metabolic.</li>
                  <li>Sometimes the sum of these four columns is less than the total deads.</li>
                  <li>Insert a new column after "Lameness Deads" named "Other Deads".</li>
                  <li>For each row, calculate:
                    <ul>
                      <li>Other Deads = Total Deads - (BRD + Lameness + Histophilus + Metabolic)</li>
                    </ul>
                  </li>
                  <li>Copy this formula down all rows to ensure totals reconcile correctly.</li>
                </ul>
              </li>
            </ol>
            <p className="mt-4 text-sm text-gray-600">
              For full details on herd-specific fixes, refer to <em>FHMS All Herd Notes</em> for 2025.
            </p>
            <hr />
          </Section>
        </Section>

        <SubHeading>Typical Template Settings for FHMS 2025</SubHeading>
        
        {/* RENDER Core Table */}
        <Paragraph><Strong>Name: FHMS 2025 - Core</Strong></Paragraph>
        <List>
          <ListItem>Level: Top Level (1)</ListItem>
          <ListItem>Type: Core (1)</ListItem>
          <ListItem>Route: n/a (0)</ListItem>
          <ListItem>Version: Version 1 (1)</ListItem>
          <ListItem>By Row or Column: By Column (2)</ListItem>
          <ListItem>Method: Populate template fields from Target (1)</ListItem>
        </List>
        <TemplateFieldsTable title="Core Template: Associated Core Template Fields" fields={coreTemplateFields} />

        {/* RENDER Feed Table */}
        <Paragraph><Strong>Name: FHMS 2025 - Drugs (Feed)</Strong></Paragraph>
        <List>
          <ListItem>Level: Second Level (2)</ListItem>
          <ListItem>Type: Drug (2)</ListItem>
          <ListItem>Route: Feed (2)</ListItem>
          <ListItem>Version: Version 1 (1)</ListItem>
          <ListItem>By Row or Column: By Row (1)</ListItem>
          <ListItem>Method: Populate template fields from Target (1)</ListItem>
        </List>
        <TemplateFieldsTable title="Feed Drug Template: Associated Drug Template Fields" fields={feedDrugTemplateFields} />

        {/* RENDER Injectable Table */}
        <Paragraph><Strong>Name: FHMS 2025 - Drugs (Injectable)</Strong></Paragraph>
        <List>
          <ListItem>Level: Second Level (2)</ListItem>
          <ListItem>Type: Drug (2)</ListItem>
          <ListItem>Route: Injectable (3)</ListItem>
          <ListItem>Version: Version 1 (1)</ListItem>
          <ListItem>By Row or Column: By Row (1)</ListItem>
          <ListItem>Method: Populate template fields from Target (1)</ListItem>
        </List>
        <TemplateFieldsTable title="Injectable Drug Template: Associated Drug Template Fields" fields={injectableDrugTemplateFields} />

        {/* RENDER Bolus Table */}
        <Paragraph><Strong>Name: FHMS 2025 - Drugs (Bolus)</Strong></Paragraph>
        <List>
          <ListItem>Level: Second Level (2)</ListItem>
          <ListItem>Type: Drug (2)</ListItem>
          <ListItem>Route: Bolus (1)</ListItem>
          <ListItem>Version: Version 1 (1)</ListItem>
          <ListItem>By Row or Column: By Row (1)</ListItem>
          <ListItem>Method: Populate template fields from Target (1)</ListItem>
        </List>
        <TemplateFieldsTable title="Bolus Drug Template: Associated Drug Template Fields" fields={bolusDrugTemplateFields} />
      </Section>
    </Container>
  );
};

export default Fhms2025Content;